import React from "react";
import dayjs from "dayjs";
import { ColoredValue } from "@src/components/ColoredValue";
import type { AppPosition } from "@src/types";
import { getPositionNetProfit } from "@src/utils/positionProfit";
import { isPositionFinanciallyClosed } from "@src/utils/positionStatus";

interface MonthData {
  month: string; // YYYY-MM format
  pnl: number;
  pnlPct: number;
}

interface Props {
  positions: AppPosition[];
  initialCapital: number;
}

const MONTHS_PER_ROW = 12;

/**
 * Displays monthly performance in a grid format.
 * Shows 12 months per row, with multiple rows as needed.
 * Calculates PnL from closed positions based on their end_ts.
 */
export const MonthlyPerformanceGrid: React.FC<Props> = ({ positions, initialCapital }) => {
  // Filter closed positions only
  const closedPositions = positions.filter(isPositionFinanciallyClosed);

  if (closedPositions.length === 0) {
    return (
      <div className="p-3 text-muted">
        <small>No closed positions available for monthly performance calculation</small>
      </div>
    );
  }

  // Group positions by month and calculate PnL
  // Note: end_ts check removed - closedPositions already filtered (by isPositionFinanciallyClosed, which requires end_ts)
  const monthlyPnl = closedPositions.reduce((acc, position) => {
    const month = dayjs(position.end_ts).format("YYYY-MM");
    const profit = getPositionNetProfit(position)!;

    acc.set(month, (acc.get(month) || 0) + profit);
    return acc;
  }, new Map<string, number>());

  // Convert to array and sort by date (oldest to newest)
  const sortedMonths = Array.from(monthlyPnl.entries())
    .map(([month, pnl]) => ({ month, pnl }))
    .sort((a, b) => a.month.localeCompare(b.month));

  // Calculate percentages based on capital at start of each month (industry standard)
  let runningCapital = initialCapital;
  const monthDataArray: MonthData[] = sortedMonths.map(({ month, pnl }) => {
    const capitalAtStartOfMonth = runningCapital;

    // If capital is already zero or negative, percentage calculation is meaningless
    // Use NaN to indicate account is blown
    const pnlPct = capitalAtStartOfMonth > 0 ? (pnl / capitalAtStartOfMonth) * 100 : NaN;

    // Update running capital for next month
    runningCapital += pnl;

    return {
      month,
      pnl,
      pnlPct,
    };
  });

  if (monthDataArray.length === 0) {
    return (
      <div className="p-3 text-muted">
        <small>No monthly performance data available</small>
      </div>
    );
  }

  // Group into rows of MONTHS_PER_ROW months each
  const rows: MonthData[][] = [];
  for (let i = 0; i < monthDataArray.length; i += MONTHS_PER_ROW) {
    rows.push(monthDataArray.slice(i, i + MONTHS_PER_ROW));
  }

  return (
    <div className="MonthlyPerformanceGrid">
      <h6 className="mb-2">Monthly Performance</h6>
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className="MonthlyPerformanceRow">
          {row.map((monthData) => {
            const monthName = dayjs(monthData.month).format("MMM YYYY");
            const isProfit = monthData.pnl > 0;
            const isLoss = monthData.pnl < 0;
            const cellClass = isProfit ? "profit" : isLoss ? "loss" : "neutral";

            return (
              <div key={monthData.month} className={`MonthlyPerformanceCell ${cellClass}`}>
                <div className="MonthLabel">{monthName}</div>
                <div className="PnlValue">
                  <ColoredValue value={monthData.pnl} type="currency" decimals={2} />
                </div>
                <div className="PnlPercentage">
                  {isNaN(monthData.pnlPct) ? (
                    "Blown"
                  ) : (
                    <ColoredValue value={monthData.pnlPct} type="percentage" decimals={2} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
